import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useAuth} from '../../auth'

/** Stable revision token for effective-permission changes (role toggle, rbac-permissions-updated). */
export function useCrewingPermissionRevision(): string {
  const {permissionsLoaded, resolvedPermissions} = useAuth()
  return useMemo(() => {
    if (!permissionsLoaded) return ''
    return Object.entries(resolvedPermissions)
      .filter(([, decision]) => decision?.allowed === true)
      .map(([code]) => code)
      .sort()
      .join('|')
  }, [permissionsLoaded, resolvedPermissions])
}

export function useCrewingCanRead(permissionCode: string): boolean {
  const {hasPermission, permissionsLoaded, isSuperAdmin} = useAuth()
  return permissionsLoaded && (isSuperAdmin || hasPermission(permissionCode))
}

type PermissionFetchOptions<T> = {
  permissionCode: string
  fetcher: () => Promise<T>
  onSuccess: (data: T) => void
  onDenied?: () => void
  onError?: (error: unknown) => void
  extraDeps?: unknown[]
}

/**
 * Runs fetcher when the read permission is granted; clears stale state when denied.
 * Re-runs automatically after profile/effective-access refresh.
 */
export function useCrewingPermissionFetch<T>({
  permissionCode,
  fetcher,
  onSuccess,
  onDenied,
  onError,
  extraDeps = [],
}: PermissionFetchOptions<T>): void {
  const canRead = useCrewingCanRead(permissionCode)
  const permissionRevision = useCrewingPermissionRevision()
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess

  const onDeniedRef = useRef(onDenied)
  onDeniedRef.current = onDenied

  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const runFetch = useCallback(async () => {
    if (!canRead) {
      onDeniedRef.current?.()
      return
    }
    try {
      const data = await fetcherRef.current()
      onSuccessRef.current(data)
    } catch (error) {
      onErrorRef.current?.(error)
    }
  }, [canRead])

  useEffect(() => {
    let cancelled = false
    const execute = async () => {
      if (!canRead) {
        onDeniedRef.current?.()
        return
      }
      try {
        const data = await fetcherRef.current()
        if (!cancelled) {
          onSuccessRef.current(data)
        }
      } catch (error) {
        if (!cancelled) {
          onErrorRef.current?.(error)
        }
      }
    }
    execute()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead, permissionRevision, runFetch, ...extraDeps])
}

export const CREWING_READ_PERMISSIONS = {
  CREW_MASTER: 'READ_CREW',
  CREW_ASSIGNMENT: 'READ_CREW_ASSIGNMENT',
  REST_HOURS: 'READ_REST_HOURS',
  OVERTIME: 'READ_OVERTIME_ENTRY',
  DEDUCTION: 'READ_DEDUCTIONS_MANAGER',
  CLAIM: 'READ_CREW_CLAIM',
  ALLOTMENT: 'READ_CREW_ALLOTMENT',
  APPRAISAL: 'READ_CREW_APPRAISALS',
  TRAINING: 'READ_TRAINING_TRACKER',
  COMPLAINT: 'READ_CREW_COMPLAINT',
} as const

/** Action-level permission codes aligned with catalogue v2.1.3 menu mappings. */
export const CREWING_ACTION_PERMISSIONS = {
  BULK_UPLOAD_CREW_MASTER: 'BULK_UPLOAD_CREW_MASTER',
  APPROVE_REJECT_CREW_MASTER: 'APPROVE_REJECT_CREW_MASTER',
  DEACTIVATE_CREW_MASTER: 'DEACTIVATE_CREW_MASTER',
  SIGN_ON_CREW_ASSIGNMENT: 'SIGN_ON_CREW_ASSIGNMENT',
  SIGN_OFF_CREW_ASSIGNMENT: 'SIGN_OFF_CREW_ASSIGNMENT',
  EXPORT_CREW_ASSIGNMENT: 'EXPORT_CREW_ASSIGNMENT',
  DOWNLOAD_CREW_ASSIGNMENT: 'DOWNLOAD_CREW_ASSIGNMENT',
  APPROVE_CREW_OVERTIME: 'APPROVE_CREW_OVERTIME',
  APPROVE_CREW_DEDUCTION: 'APPROVE_CREW_DEDUCTION',
  EXPORT_CREW_DEDUCTION: 'EXPORT_CREW_DEDUCTION',
  DOWNLOAD_CREW_DEDUCTION: 'DOWNLOAD_CREW_DEDUCTION',
  SALARY_SLIP_CREW_ALLOTMENT: 'SALARY_SLIP_CREW_ALLOTMENT',
  PORTAGE_BILL_CREW_ALLOTMENT: 'PORTAGE_BILL_CREW_ALLOTMENT',
  MANAGE_CREW_ALLOTMENT_DRAFT: 'MANAGE_CREW_ALLOTMENT_DRAFT',
  MANAGE_CREW_CLAIM_DRAFT: 'MANAGE_CREW_CLAIM_DRAFT',
  MANAGE_CREW_DEDUCTION_DRAFT: 'MANAGE_CREW_DEDUCTION_DRAFT',
  EXPORT_CREW_ALLOTMENT: 'EXPORT_CREW_ALLOTMENT',
  DOWNLOAD_CREW_ALLOTMENT: 'DOWNLOAD_CREW_ALLOTMENT',
  EXPORT_CREW_CLAIM: 'EXPORT_CREW_CLAIM',
  DOWNLOAD_CREW_CLAIM: 'DOWNLOAD_CREW_CLAIM',
  APPROVE_CREW_APPRAISAL: 'APPROVE_CREW_APPRAISAL',
  APPROVE_CREW_TRAINING: 'APPROVE_CREW_TRAINING',
  APPROVE_CREW_COMPLAINTS: 'APPROVE_CREW_COMPLAINTS',
} as const
